<script lang="ts">
  import { onMount } from 'svelte';
  import { api } from '../../lib/api/client';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import { confirm } from '../../lib/stores/confirmDialog';
  import { age } from '../../lib/utils/time';
  import { t } from '../../lib/i18n/index.ts';

  // Forwarding sends the member's own locations and place arrivals and
  // departures, as OwnTracks messages, to destinations they run, such as Home
  // Assistant or OwnTracks Recorder (spec 012). The server admin decides
  // whether it is allowed.
  type Forward = {
    id: string;
    kind: 'http' | 'mqtt';
    url: string;
    username: string;
    has_password: boolean;
    topic: string;
    active: boolean;
    last_ok?: string;
    last_error: string;
    last_error_at?: string;
  };
  type Forwards = { allowed: boolean; private_allowed: boolean; forwards: Forward[] };

  let info = $state<Forwards | null>(null);
  let refused = $state(false);
  let error = $state('');
  let editing = $state<Partial<Forward> & { password?: string } | null>(null);
  let tested = $state<Record<string, string>>({});

  async function load() {
    try {
      info = await api.get<Forwards>('/me/forwards');
    } catch (e: any) {
      // A child's account has no forwarding of its own (spec 001).
      if (e.code === 'FORBIDDEN') refused = true;
      else error = e.message;
    }
  }
  onMount(load);

  function add() {
    editing = { kind: 'http', url: '', username: '', topic: 'owntracks', active: true, password: '' };
  }

  async function save(e: SubmitEvent) {
    e.preventDefault();
    if (!editing) return;
    error = '';
    const body: Record<string, unknown> = { kind: editing.kind, url: editing.url, username: editing.username, topic: editing.topic, active: editing.active };
    // An empty password field on an existing destination keeps its password.
    if (!editing.id || editing.password) body.password = editing.password ?? '';
    try {
      if (editing.id) await api.patch(`/me/forwards/${editing.id}`, body);
      else await api.post('/me/forwards', body);
      editing = null;
      await load();
    } catch (err: any) {
      error = err.message;
    }
  }

  async function toggle(f: Forward) {
    await api.patch(`/me/forwards/${f.id}`, { active: !f.active }).catch((e: any) => (error = e.message));
    await load();
  }

  async function remove(f: Forward) {
    if (!(await confirm(t('settingsForwardStopConfirm', { url: f.url })))) return;
    await api.delete(`/me/forwards/${f.id}`).catch((e: any) => (error = e.message));
    await load();
  }

  async function test(f: Forward) {
    tested[f.id] = t('settingsForwardSending');
    try {
      const r = await api.post<{ ok: boolean; error?: string }>(`/me/forwards/${f.id}/test`, {});
      tested[f.id] = r.ok ? t('settingsForwardDelivered') : t('settingsForwardFailed', { error: r.error });
    } catch (e: any) {
      tested[f.id] = t('settingsForwardFailed', { error: e.message });
    }
    await load();
  }

  // kind names how a destination is reached and as whom.
  function kind(f: Forward): string {
    if (f.kind === 'mqtt') return f.username ? t('settingsForwardMqttTopicAs', { topic: f.topic, user: f.username }) : t('settingsForwardMqttTopic', { topic: f.topic });
    return f.username ? t('settingsForwardHttpAs', { user: f.username }) : t('settingsForwardHttp');
  }
</script>

{#if !refused && info}
  <div class="card p-5 mb-6">
    <PageHeader title={t('settingsForwardTitle')} level="section">
      {#if info.allowed}
        <button class="btn btn-secondary btn-sm" onclick={add}><i aria-hidden="true" class="las la-plus mr-1"></i>{t('settingsForwardAdd')}</button>
      {/if}
    </PageHeader>
    {#if !info.allowed}
      <p class="text-sm text-text-muted">
        {t('settingsForwardOff')}
      </p>
    {:else}
      <p class="text-sm text-text-muted mb-3">
        {info.private_allowed ? t('settingsForwardAbout') : t('settingsForwardAboutNoPrivate')}
      </p>
      {#if info.forwards.length === 0 && !editing}
        <p class="text-sm">{t('settingsForwardNone')}</p>
      {/if}
      <ul>
        {#each info.forwards as f (f.id)}
          <li class="py-2 flex flex-wrap items-center gap-3">
            <i aria-hidden="true" class="las {f.kind === 'mqtt' ? 'la-broadcast-tower' : 'la-globe'} text-xl"></i>
            <span class="flex-1 min-w-0">
              <span class="font-medium break-all">{f.url}</span>
              <span class="block text-xs text-text-muted">
                {kind(f)} ·
                {#if !f.active}{t('settingsForwardPaused')}{:else if f.last_error}{#if f.last_error_at}{@const at = t('settingsForwardErrorAt', { error: '\u0000', when: age(f.last_error_at) }).split('\u0000')}{at[0]}<span style="color: var(--theme-error);">{f.last_error}</span>{at[1]}{:else}<span style="color: var(--theme-error);">{f.last_error}</span>{/if}{:else if f.last_ok}{t('settingsForwardLastDelivered', { when: age(f.last_ok) })}{:else}{t('settingsForwardNothingSent')}{/if}
              </span>
              {#if tested[f.id]}<span class="block text-xs">{tested[f.id]}</span>{/if}
            </span>
            <button class="btn btn-secondary btn-sm" onclick={() => test(f)}>{t('settingsForwardSendTest')}</button>
            <button class="btn btn-secondary btn-sm" onclick={() => toggle(f)}>{f.active ? t('settingsForwardPause') : t('settingsForwardResume')}</button>
            <button class="btn btn-secondary btn-sm" onclick={() => (editing = { ...f, password: '' })}>{t('appEdit')}</button>
            <button class="btn btn-danger btn-sm" onclick={() => remove(f)} aria-label={t('settingsForwardRemove', { url: f.url })}><i aria-hidden="true" class="las la-trash"></i></button>
          </li>
        {/each}
      </ul>
      {#if editing}
        <form class="mt-3 flex flex-col gap-3 border-t pt-3" style="border-color: var(--theme-surface0);" onsubmit={save}>
          <div class="flex gap-4 text-sm">
            <label class="flex items-center gap-1"><input type="radio" bind:group={editing.kind} value="http" />{t('settingsForwardHttp')}</label>
            <label class="flex items-center gap-1"><input type="radio" bind:group={editing.kind} value="mqtt" />{t('settingsForwardMqtt')}</label>
          </div>
          <label class="text-sm">
            {t('settingsForwardAddress')}
            <input class="input w-full mt-1" type="url" required maxlength="2048" bind:value={editing.url}
              placeholder={editing.kind === 'mqtt' ? 'mqtts://broker.example.org' : 'https://homeassistant.example.org/api/webhook/…'} />
          </label>
          <div class="grid sm:grid-cols-2 gap-3">
            <label class="text-sm">{t('settingsForwardUserName')} <input class="input w-full mt-1" maxlength="256" bind:value={editing.username} autocomplete="off" /></label>
            <label class="text-sm">
              {t('settingsPassword')}
              <input class="input w-full mt-1" type="password" maxlength="256" bind:value={editing.password} autocomplete="new-password"
                placeholder={editing.id && editing.has_password ? t('settingsForwardUnchanged') : ''} />
            </label>
          </div>
          {#if editing.kind === 'mqtt'}
            <label class="text-sm">{t('settingsForwardTopic')} <input class="input w-full mt-1" maxlength="64" bind:value={editing.topic} /></label>
            <p class="text-xs text-text-muted">{t('settingsForwardTopicNote')}</p>
          {/if}
          <div class="flex justify-end gap-2">
            <button type="button" class="btn btn-secondary" onclick={() => (editing = null)}>{t('appCancel')}</button>
            <button type="submit" class="btn btn-primary">{t('appSave')}</button>
          </div>
        </form>
      {/if}
    {/if}
    {#if error}<p class="text-sm mt-2" style="color: var(--theme-error);">{error}</p>{/if}
  </div>
{/if}
